import { getCDNBaseURL } from 'envUtils';
import PropTypes from 'prop-types';

import React, { useState } from 'react';

const ImageTiny = ({ alt, src, ...props }) => {
  return (
    <GenericImage
      alt={typeof alt !== 'string' ? '' : alt}
      filePath={src}
      height={50}
      width={50}
      {...props}
    />
  );
};

const ImageLarge = ({ alt, src, ...props }) => {
  return (
    <GenericImage
      alt={typeof alt !== 'string' ? '' : alt}
      filePath={src}
      height={480}
      width={480}
      {...props}
    />
  );
};

const ImageMedium = ({ alt, src, ...props }) => {
  return (
    <GenericImage
      alt={typeof alt !== 'string' ? '' : alt}
      filePath={src}
      height={200}
      width={200}
      {...props}
    />
  );
};

const ImageThumbnail = ({ alt, src, ...props }) => {
  return (
    <GenericImage
      alt={typeof alt !== 'string' ? '' : alt}
      filePath={src}
      height={80}
      width={80}
      {...props}
    />
  );
};

// original size
const Image = ({ alt, src, ...props }) => {
  return (
    <GenericImage
      alt={typeof alt !== 'string' ? '' : alt}
      filePath={src}
      {...props}
    />
  );
};

const propTypes = {
  alt: PropTypes.string,
  src: PropTypes.string,
};

ImageTiny.propTypes = { ...propTypes };
ImageLarge.propTypes = { ...propTypes };
ImageMedium.propTypes = { ...propTypes };
ImageLarge.propTypes = { ...propTypes };
Image.propTypes = { ...propTypes };

export default Image;

const thumborCDNBaseURLs = [
  'https://cdn.financetechtest.com/',
  'https://cdnv2.financetechtest.com/',
  'https://cdn.financetech365.com/',
  'https://cdnv2.financetech365.com/',
];

const GenericImage = ({
  alt,
  aspectRatio,
  baseURL = getCDNBaseURL(),
  filePath = 'ft_generic/no_photo.svg',
  height,
  width,
  responsive,
  pictureStyle,
  style,
  // thumbor props
  // https://docs.aws.amazon.com/solutions/latest/serverless-image-handler/thumbor-filters.html
  // https://thumbor.readthedocs.io/en/latest/filters.html#available-filters
  autoJPG,
  backgroundColor,
  blur,
  colorFill,
  convolution,
  crop,
  equalize,
  grayscale,
  imageFormat,
  noUpscale,
  proportion,
  quality,
  rgb,
  rotate,
  sharpen,
  stretch,
  stripEXIF,
  stripICC,
  upscale,
  watermark,
  // <img> props
  ...otherProps
}) => {
  const [isLoading, setIsLoading] = useState(true);

  if (!filePath || filePath === 'undefined') {
    baseURL = getCDNBaseURL();
    filePath = 'ft_generic/no_photo.svg';
  }

  if (typeof filePath === 'string' && /^http(s)?:\/\//i.test(filePath)) {
    const sourceURL = new URL(filePath);

    baseURL = `${sourceURL.origin}/`;
    filePath = filePath.replace(baseURL, '');
  }

  if (!baseURL?.endsWith('/')) {
    baseURL += '/';
  }

  const isThumborCDN = thumborCDNBaseURLs.some((url) =>
    baseURL?.startsWith(url),
  );

  if (filePath?.startsWith('/')) {
    filePath = filePath?.slice(1);
  }

  let thumbor = '';
  let dimensions = '';
  let sources;

  if (isThumborCDN) {
    if (autoJPG) {
      thumbor += `filters:autojpg()/`;
    }

    if (backgroundColor) {
      thumbor += `filters:background_color(${backgroundColor})/`;
    }

    if (blur) {
      thumbor += `filters:blur(${blur})/`;
    }

    if (colorFill) {
      thumbor += `filters:fill(${colorFill})/`;
    }

    if (convolution) {
      thumbor += `filters:convolution(${convolution})/`;
    }

    if (crop) {
      thumbor += `${crop}`;
    }

    if (equalize) {
      thumbor += `filters:equalize()/`;
    }

    if (grayscale) {
      thumbor += `filters:grayscale()/`;
    }

    if (imageFormat) {
      thumbor += `filters:format(${imageFormat})/`;
    }

    if (noUpscale) {
      thumbor += `filters:no_upscale()/`;
    }

    if (proportion) {
      thumbor += `filters:proportion(${proportion})/`;
    }

    if (quality) {
      thumbor += `filters:quality(${quality})/`;
    }

    if (height || width) {
      dimensions = `fit-in/${width || 0}x${height || 0}/`;
    }

    if (rgb) {
      thumbor += `filters:rgb(${rgb})/`;
    }

    if (rotate) {
      thumbor += `filters:rotate(${rotate})/`;
    }

    if (sharpen) {
      thumbor += `filters:sharpen(${sharpen})/`;
    }

    if (stretch) {
      thumbor += `filters:stretch()/`;
    }

    if (stripEXIF) {
      thumbor += `filters:strip_exif()/`;
    }

    if (stripICC) {
      thumbor += `filters:strip_icc()/`;
    }

    if (upscale) {
      thumbor += `filters:upscale()/`;
    }

    if (watermark) {
      thumbor += `filters:watermark(${watermark})/`;
    }
  }

  if (responsive && isThumborCDN) {
    // don't replace <Fragment></Fragment> with <></>
    // or it will cause a "Fragment is not defined" error
    sources = (
      <>
        <source
          media='(min-width:1920px)'
          srcSet={`${baseURL}fit-in/1920x0/${thumbor}${filePath} 1920w`}
        />
        <source
          media='(min-width:1080px)'
          srcSet={`${baseURL}fit-in/1080x0/${thumbor}${filePath} 1080w,
                  ${baseURL}fit-in/1920x0/${thumbor}${filePath} 2x`}
        />
        <source
          media='(min-width:768px)'
          srcSet={`${baseURL}fit-in/768x0/${thumbor}${filePath} 768w,
                  ${baseURL}fit-in/1080x0/${thumbor}${filePath} 1.5x`}
        />
        <source
          srcSet={`${baseURL}fit-in/375x0/${thumbor}${filePath} 375w,
                  ${baseURL}fit-in/768x0/${thumbor}${filePath} 2x`}
        />
      </>
    );
  } else {
    sources = (
      <source srcSet={`${baseURL}${dimensions}${thumbor}${filePath}`} />
    );
  }

  return (
    <picture style={{ lineHeight: 0, ...pictureStyle }}>
      {sources}

      <img
        src={`${baseURL}${dimensions}${thumbor}${filePath}`}
        alt={alt}
        style={{
          maxInlineSize: '100%',
          aspectRatio: `${aspectRatio}`,
          objectFit: 'contain',
          width: '100%',
          backgroundColor: isLoading ? 'rgba(0, 0, 0, 0.11)' : 'transparent',
          ...style,
        }}
        {...otherProps}
        onLoad={() => setIsLoading(false)}
      />
    </picture>
  );
};

export { ImageTiny, ImageLarge, ImageMedium, ImageThumbnail };
